import { RANGE_LABELS, RANGE_ORDER, rangeBounds } from './dashboardRange';
import type { DateRange, RangeKey } from './dashboardRange';
import type { Lead } from './types';

/**
 * The Pipeline's time range: the Dashboard's five, plus All time.
 *
 * Kyu, 1 Sep: "do we have time range option as well like the Dashboard?"
 *
 * ── Why this wraps the Dashboard's ranges rather than redefining them ─────
 *
 * Monday-start weeks, UTC throughout, and an end that is never in the future
 * are all decided in dashboardRange.ts and argued for there. Two modules
 * disagreeing about when a week starts is the shape of bug that makes one
 * screen say 14 and the other 16 about the same seven days.
 *
 * ── Why ALL TIME exists here and not on the Dashboard ────────────────────
 *
 * The Dashboard is a Monday-morning page: it opens on the week it is in, and
 * that is the right default for "how are we doing". The Pipeline is the
 * in-the-weeds view of a cohort, and it has always shown everything. Opening
 * it on This Week would silently drop 90% of the leads somebody was looking at
 * yesterday, which reads as data loss rather than as a filter.
 *
 * So All time is first, and it is the default.
 *
 * ── What the range MEANS, which is the part worth getting right ──────────
 *
 * It filters on when the lead ARRIVED. Everything else on the row is then
 * "ever, for those leads" — emailed ever, replied ever, won ever.
 *
 * The alternative reading is to window each column on its own date: emails
 * sent this week, replies received this week. That is what the Dashboard
 * does, and it is right there, because the Dashboard reports ACTIVITY.
 *
 * It would be wrong here. This table's whole shape is a cohort walking
 * through stages — They arrived, We reached out, What came of it — and
 * windowing each column separately would divide replies from leads that
 * arrived last year by leads that arrived this week. The conversion rate
 * would be a number true of nothing.
 */

export type PipelineRangeKey = 'all' | RangeKey;

/** All time first: it is the default, and the list reads shortest-to-longest after it. */
export const PIPELINE_RANGE_ORDER: PipelineRangeKey[] = ['all', ...RANGE_ORDER];

export const PIPELINE_RANGE_LABELS: Record<PipelineRangeKey, string> = {
  all: 'All time',
  ...RANGE_LABELS,
};

export const DEFAULT_PIPELINE_RANGE: PipelineRangeKey = 'all';

/** The days a range covers, or null for All time — which has no bounds to draw. */
export function pipelineRangeBounds(key: PipelineRangeKey, today: string): DateRange | null {
  return key === 'all' ? null : rangeBounds(key, today);
}

/**
 * The leads that ARRIVED inside the range.
 *
 * `created_at` is a timestamp and the bounds are days, so it is compared on
 * the day part alone. Comparing the raw timestamp against a bare `to` date
 * would silently exclude everything that arrived after midnight on the last
 * day of the window — which on "This Week" is most of today.
 */
export function leadsInRange<T extends Pick<Lead, 'created_at'>>(
  leads: readonly T[],
  bounds: DateRange | null,
): T[] {
  if (bounds === null) {
    return [...leads];
  }
  return leads.filter((lead) => {
    const day = lead.created_at.slice(0, 10);
    return day >= bounds.from && day <= bounds.to;
  });
}

/**
 * How the table says what window it is showing.
 *
 * "All time" gets no phrase at all — a table with no window should not carry
 * words explaining that it has no window.
 */
export function pipelineRangeNote(key: PipelineRangeKey, bounds: DateRange | null): string {
  if (key === 'all' || bounds === null) {
    return '';
  }
  return `${PIPELINE_RANGE_LABELS[key]} · arrived ${bounds.from} to ${bounds.to}`;
}
